import React from 'react';
import { Text } from '@ui';
import { FilePreviewThumb } from '@ui/molecules/FileHandler/FilePreviewThumb';
import { translate } from '@utils/i18n';
import { AttachmentNameCell } from '../components/AttachmentNameCell';
import { AttachmentCategoryCell } from '../components/AttachmentCategoryCell';
import { AttachmentVisibilityCell } from '../components/AttachmentVisibilityCell';
const MASS_ACTIONS_NS = 'features.building_attachments.mass_actions';
export const SORTABLE_COLUMNS = [
    'name',
    'categoryLabel',
    'visibilitySortKey',
    'typeLabel',
    'uploadedSortValue',
    'sizeBytes'
];
function PreviewCell({ item, onPreviewClick }) {
    const thumbKind = item.previewKind === 'photo' || item.previewKind === 'pdf' ? item.previewKind : 'other';
    const thumb = React.createElement(FilePreviewThumb, { kind: thumbKind, previewUrl: item.url, downloadUrl: item.url });
    return (React.createElement("button", { type: "button", className: "building-attachments-table__preview-cell", "aria-label": `Preview ${item.name}`, onClick: (event) => {
            event.stopPropagation();
            onPreviewClick(item);
        } }, thumb));
}
function UploadedCell({ item }) {
    return React.createElement(Text, { variant: "body" }, item.uploadedLabel);
}
function SizeCell({ item }) {
    return React.createElement(Text, { variant: "body" }, item.sizeLabel);
}
function ActionsCell({ item, onRequestRowDelete }) {
    return (React.createElement("button", { type: "button", className: "building-attachments-table__row-delete", "aria-label": translate('row_delete_aria_label', { namespace: MASS_ACTIONS_NS, fileName: item.name }), onClick: (event) => {
            event.stopPropagation();
            onRequestRowDelete(item);
        } },
        React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" })));
}
/**
 * Builds the table's column definitions. Sort comparators read `dataKey` off
 * the row (Table's own internal client-side sort), so columns whose display
 * differs from their sort value (Visible to, Uploaded, Size) point `dataKey`
 * at a derived numeric field and use a `renderer` for the formatted display —
 * Size in particular must sort as bytes, never as the formatted string
 * ("640 KB" must not sort above "24.6 MB").
 */
export function buildAttachmentColumns({ translateFn, onPreviewClick, buildingId, categoryOptions, addCategoryLabel, onRequestAddCategory, showPortalVisibilityOption, onRequestRowDelete }) {
    return [
        {
            // Pinned with the selection checkbox and Name so the row stays identifiable while the
            // table scrolls horizontally (per the signed-off design). `width` is mandatory on every
            // pinned column except the last — Name is last, so it stays fluid. The action column is
            // deliberately NOT pinned.
            dataKey: 'preview',
            pinned: true,
            label: translateFn('columns.preview'),
            width: '64px',
            renderer: ({ item }) => React.createElement(PreviewCell, { item: item, onPreviewClick: onPreviewClick })
        },
        {
            dataKey: 'name',
            label: translateFn('columns.name'),
            columnType: 'primary',
            pinned: true,
            renderer: ({ item }) => React.createElement(AttachmentNameCell, { item: item, buildingId: buildingId })
        },
        {
            dataKey: 'categoryLabel',
            label: translateFn('columns.category'),
            width: '180px',
            renderer: ({ item }) => (React.createElement(AttachmentCategoryCell, { item: item, buildingId: buildingId, options: categoryOptions, addCategoryLabel: addCategoryLabel, onRequestAddCategory: onRequestAddCategory }))
        },
        {
            dataKey: 'visibilitySortKey',
            label: translateFn('columns.visible_to'),
            width: '180px',
            renderer: ({ item }) => (React.createElement(AttachmentVisibilityCell, { item: item, buildingId: buildingId, showPortalOption: showPortalVisibilityOption, translateFn: translateFn }))
        },
        {
            dataKey: 'typeLabel',
            label: translateFn('columns.type'),
            width: '72px'
        },
        {
            dataKey: 'uploadedSortValue',
            label: translateFn('columns.uploaded'),
            renderer: ({ item }) => React.createElement(UploadedCell, { item: item })
        },
        {
            dataKey: 'sizeBytes',
            label: translateFn('columns.size'),
            renderer: ({ item }) => React.createElement(SizeCell, { item: item })
        },
        {
            dataKey: 'actions',
            label: '',
            columnType: 'action',
            renderer: ({ item }) => React.createElement(ActionsCell, { item: item, onRequestRowDelete: onRequestRowDelete })
        }
    ];
}
